import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header, { DEFAULT_MOBILE_LINKS } from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import Seo from '../components/Seo.jsx';
import { useScrollReveal } from '../hooks/useScrollReveal.js';
import { useNavTone } from '../hooks/useNavTone.js';
import { openApp } from '../lib/appUrl.js';
import heroImg from '../assets/BB/pexels-shuaizhi-tian-485596-29900805.jpg';
import inlineImg from '../assets/BB/pexels-tomfisk-6594779.jpg';
import related1Img from '../assets/BB/pexels-unlime-8181296.jpg';
import related2Img from '../assets/BB/pexels-keremkaplan-33124631.jpg';
import related3Img from '../assets/BB/pexels-ehaan-deva-2149036462-32358148.jpg';
import './BlogArticle.css';

// This page has only one static demo article — every "keep reading"/related
// link across the site points back at this exact same URL, so there is no
// per-article id or route here, just the one article's content as authored.
const NAV_SECTIONS = [
  ['header', 'light'],
  ['cta', 'dark'],
];

const RELATED_ARTICLES = [
  {
    image: related1Img,
    category: 'Market Insight',
    title: 'What makes a hoarding location worth the premium',
    initials: 'AG',
    byline: (
      <>Ananya Gupta &nbsp;&middot;&nbsp; Apr 2026 &nbsp;&middot;&nbsp; 4 min read</>
    ),
    delay: '1',
  },
  {
    image: related2Img,
    category: 'Advertiser Guide',
    title: 'Billboard vs. digital screen: choosing the right format for your budget',
    initials: 'RV',
    byline: (
      <>Rohan Verma &nbsp;&middot;&nbsp; Mar 2026 &nbsp;&middot;&nbsp; 6 min read</>
    ),
    delay: '2',
  },
  {
    image: related3Img,
    category: 'Advertiser Guide',
    title: 'Creative that works at 60 km/h: designing for outdoor attention spans',
    initials: 'RV',
    byline: (
      <>Rohan Verma &nbsp;&middot;&nbsp; Jan 2026 &nbsp;&middot;&nbsp; 5 min read</>
    ),
    delay: '3',
  },
];

function RelatedCard({ image, category, title, initials, byline, delay }) {
  return (
    <Link to="/blog-article" className="blog-card reveal" data-d={delay}>
      <div className="blog-card-thumb" style={{ backgroundImage: `url(${image})` }}>
        <span className="blog-card-cat">{category}</span>
      </div>
      <div className="blog-card-body">
        <h3 className="blog-card-title">{title}</h3>
        <div className="blog-card-author">
          <div className="blog-card-author-av" aria-hidden="true">{initials}</div>
          <div className="blog-card-byline">{byline}</div>
        </div>
      </div>
    </Link>
  );
}

export default function BlogArticle() {
  useScrollReveal();
  useNavTone(NAV_SECTIONS);

  // Mirrors the original's copy-link-btn script: copy the current URL and
  // flash the icon to a checkmark for 2 seconds. The LinkedIn/X share
  // buttons next to it have no click handler in the source at all — they're
  // decorative, so they stay inert here too.
  const [linkCopied, setLinkCopied] = useState(false);
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  return (
    <>
      <Seo
        title="How to Plan Your First OOH Campaign — From Brief to Billboard | FlipAd"
        description="Before you pick a site, answer three questions: where does your audience move through the city, when does this message need to land, and what does the creative need to do at distance? A framework built with 50+ first-time OOH advertisers."
        path="/blog-article"
        type="article"
      />
      <Header mobileLinks={DEFAULT_MOBILE_LINKS} activeNavKey={null} />

      {/* ════════════════════════════════════════════════
          ARTICLE HEADER
      ════════════════════════════════════════════════ */}
      <section className="article-header" id="header">
        <div className="container-md">
          <div className="reveal">
            <nav className="article-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <i className="ph ph-caret-right" style={{ fontSize: 10 }} />
              <Link to="/blog">Insights</Link>
              <i className="ph ph-caret-right" style={{ fontSize: 10 }} />
              <span>How to plan your first OOH campaign</span>
            </nav>

            <span className="article-cat">Advertiser Guide</span>
            <h1 className="article-title">How to plan your first OOH campaign — from brief to billboard</h1>
            <p className="article-excerpt">Before you pick a site, answer three questions: where does your audience move through the city, when does this message need to land, and what does the creative need to do at distance? A framework built with 50+ first-time OOH advertisers.</p>

            <div className="article-meta-row">
              <div className="article-author">
                <div className="article-author-av" aria-hidden="true">FR</div>
                <div>
                  <div className="article-author-name">FlipAd Research Team</div>
                  <div className="article-author-sub">May 28, 2026 &nbsp;&middot;&nbsp; 8 min read</div>
                </div>
              </div>
              <div className="article-share" aria-label="Share this article">
                <button className="article-share-btn" aria-label="Share on LinkedIn"><i className="ph ph-linkedin-logo" aria-hidden="true" /></button>
                <button className="article-share-btn" aria-label="Share on X"><i className="ph ph-x-logo" aria-hidden="true" /></button>
                <button className="article-share-btn" id="copy-link-btn" aria-label="Copy link" title="Copy link" onClick={handleCopyLink}>
                  <i className={linkCopied ? 'ph ph-check' : 'ph ph-link'} aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="article-hero-img reveal" style={{ backgroundImage: `url(${heroImg})` }} role="img" aria-label="City billboard at dusk" />

      {/* ════════════════════════════════════════════════
          ARTICLE BODY
      ════════════════════════════════════════════════ */}
      <section className="article-body-sec">
        <div className="container-md">
          <div className="article-body reveal">

            <p className="lede">Most first-time OOH campaigns fail for a reason that has nothing to do with the site or the price — they skip straight to "which billboard looks good" without answering three questions that should come first. Get those right, and the media plan almost writes itself.</p>

            <h2>1. Where does your audience actually move through the city?</h2>
            <p>OOH works on proximity and repetition, not reach in the abstract. A site near your customer's home means nothing if they never drive past it on the way to work, to the gym, or to the mall where they'll actually make the purchase decision. Before browsing inventory, map the two or three routes your audience takes most — commute corridors, weekend leisure routes, or the roads around a specific catchment like a business district or college campus.</p>
            <p>This is also where format matters more than most advertisers expect. A billboard on a highway commands attention for 3–4 seconds at speed; a transit panel or metro screen gets 30+ seconds of near-static dwell time. Match the format to how your audience actually experiences the space, not just to what looks impressive in a deck.</p>

            <blockquote className="article-blockquote">
              "The best OOH placements aren't the biggest or the cheapest — they're the ones sitting exactly where a decision is already being made."
            </blockquote>

            <h2>2. When does this message need to land?</h2>
            <p>Timing in OOH is two separate decisions: the campaign window, and the daypart within it. A launch campaign for a new restaurant should probably run in the two weeks before opening and the four weeks after, concentrated around lunch and dinner commute windows. A finance brand running a tax-season push cares less about time of day and more about not missing the compliance deadline entirely.</p>
            <ul>
              <li><strong>Awareness campaigns</strong> generally need 3–4 weeks minimum for repetition to compound — anything shorter reads as a blip, not a presence.</li>
              <li><strong>Response-driven campaigns</strong> (a sale, an event, a launch date) should book with buffer — printing, installation, and site clearance can take 5–7 days depending on the format.</li>
              <li><strong>Always-on or defensive campaigns</strong> (protecting share in a category) benefit from rotating across sites rather than holding one location indefinitely.</li>
            </ul>

            <h2>3. What does the creative need to do at distance?</h2>
            <p>A poster that reads perfectly on a phone screen often fails completely at 80 metres and 60 km/h. The general rule for roadside formats: one message, one visual anchor, and a headline short enough to read in the time it takes to pass. If your creative needs more than 5–6 words to land the point, it's built for a different medium.</p>

            <div className="article-inline-img">
              <img src={inlineImg} alt="Billboard installation crew mounting a large-format print" />
            </div>
            <p className="article-inline-caption">Legibility testing at distance matters more than how the creative looks on a laptop screen.</p>

            <p>Digital screens loosen this constraint slightly — you can sequence two or three short frames — but the same principle holds: attention is measured in seconds, not minutes. If in doubt, print a scaled mock at actual size and view it from across a parking lot before committing.</p>

            <h2>Putting it together</h2>
            <p>Once you know the route, the window, and the creative constraint, choosing between specific listings becomes a filtering exercise rather than a guessing game. On FlipAd, you can search by city, format, and date range, compare verified pricing side by side, and lock in a booking through escrow — with your payment released to the space owner only once proof of display comes in. No broker calls, no advance-payment leap of faith.</p>
            <p>Start with the three questions above, and the rest of the plan follows.</p>

          </div>

          <div className="article-bio reveal">
            <div className="article-bio-av" aria-hidden="true">FR</div>
            <div>
              <div className="article-bio-name">FlipAd Research Team</div>
              <div className="article-bio-desc">The FlipAd Research Team publishes data-backed guides on planning, booking, and measuring out-of-home advertising in India, drawn from patterns across thousands of campaigns run on the platform.</div>
            </div>
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          RELATED ARTICLES
      ════════════════════════════════════════════════ */}
      <section className="related sec" aria-label="Related articles">
        <div className="container">
          <div className="sec-header reveal">
            <p className="eyebrow">Keep reading</p>
            <h2 className="sec-title" style={{ fontSize: 'clamp(var(--lumen-text-2xl),3vw,var(--lumen-text-3xl))' }}>Related articles</h2>
          </div>

          <div className="blog-grid">
            {RELATED_ARTICLES.map((article) => (
              <RelatedCard key={article.title} {...article} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════════════════
          CLOSING CTA
      ════════════════════════════════════════════════ */}
      <section className="so-cta sec" id="cta" aria-labelledby="cta-hl">
        <div className="container">
          <div className="reveal">
            <p className="eyebrow">Get started</p>
            <h2 className="so-cta-hl" id="cta-hl">Ready to book your first OOH campaign?</h2>
            <p className="so-cta-sub">1,240+ verified spaces across 23 cities. Browse for free, book in minutes.</p>
            <div className="so-cta-btns">
              <button type="button" className="lmn-btn primary lg" onClick={() => openApp('/inventory')}>
                Browse inventory <i className="ph ph-arrow-right" aria-hidden="true" />
              </button>
              <Link to="/blog" className="lmn-btn outline-white lg">
                More insights
              </Link>
            </div>
            <p className="so-cta-link">Have questions? <a href="mailto:support@flipad.in" style={{ color: 'color-mix(in srgb,#fff 60%,transparent)', textDecoration: 'underline', textUnderlineOffset: '3px' }}>Contact us →</a></p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
